import {
  GooglePay,
  GooglePay_Gateway,
  Money,
  PlainMessage,
} from "frontend-client";

export const getConvertedValue = (value?: PlainMessage<Money>): string => {
  let convertedValue = 0;

  if (!value) return `${convertedValue}`;
  const decimalCount = value.decimalCount ?? 0;

  convertedValue = Number(value.amount) / Math.pow(10, decimalCount);

  if (isNaN(convertedValue)) {
    convertedValue = 0;
  }

  return convertedValue.toFixed(decimalCount);
};

type GetMerchantInfoProps = {
  googlePaySettings?: PlainMessage<GooglePay>;
};

export function getMerchantInfo({
  googlePaySettings,
}: GetMerchantInfoProps): google.payments.api.MerchantInfo {
  return {
    merchantName: googlePaySettings?.googlePayMerchantName,
    merchantId: googlePaySettings?.googlePayMerchantId || "",
  };
}

type GetTransactionInfoProps = {
  country: string;
  currencyCode?: string;
  totalAmount?: PlainMessage<Money>;
};

export function getTransactionInfo({
  country,
  currencyCode,
  totalAmount,
}: GetTransactionInfoProps): google.payments.api.TransactionInfo {
  return {
    countryCode: country,
    currencyCode: totalAmount?.currencyCode ?? currencyCode ?? "",
    totalPriceStatus: "FINAL",
    totalPrice: getConvertedValue(totalAmount),
  };
}

type AllowedPaymentMethods =
  | google.payments.api.IsReadyToPayPaymentMethodSpecification[]
  | google.payments.api.PaymentMethodSpecification[];

type GetIsReadyToPayRequestProps = {
  allowedPaymentMethods: AllowedPaymentMethods;
};

export function getIsReadyToPayRequest({
  allowedPaymentMethods,
}: GetIsReadyToPayRequestProps): google.payments.api.IsReadyToPayRequest {
  return {
    apiVersion: 2,
    apiVersionMinor: 0,
    allowedPaymentMethods,
  };
}

export function getAllowedPaymentMethods(
  googlePaySettings: PlainMessage<GooglePay>,
  allowedCardNetworks: google.payments.api.CardNetwork[]
): AllowedPaymentMethods {
  return [
    {
      type: "CARD",
      parameters: {
        allowedAuthMethods: ["PAN_ONLY"],
        allowedCardNetworks,
        billingAddressRequired: true,
        billingAddressParameters: {
          format: "FULL",
        },
      },
      tokenizationSpecification: {
        type: "PAYMENT_GATEWAY",
        parameters: {
          gateway:
            googlePaySettings.gateway === GooglePay_Gateway.CHASE
              ? "chase"
              : undefined,
          gatewayMerchantId: googlePaySettings?.gatewayMerchantId,
        },
      } as google.payments.api.PaymentGatewayTokenizationSpecification,
    },
  ];
}

type GetPaymentDataRequestProps = {
  googlePaySettings: PlainMessage<GooglePay>;
  country: string;
  currencyCode?: string;
  totalAmount?: PlainMessage<Money>;
  allowedPaymentMethods: AllowedPaymentMethods;
};

export function getPaymentDataRequest({
  googlePaySettings,
  country,
  currencyCode,
  totalAmount,
  allowedPaymentMethods,
}: GetPaymentDataRequestProps): google.payments.api.PaymentDataRequest {
  return {
    apiVersion: 2,
    apiVersionMinor: 0,
    transactionInfo: getTransactionInfo({ country, currencyCode, totalAmount }),
    merchantInfo: getMerchantInfo({ googlePaySettings }),
    allowedPaymentMethods:
      allowedPaymentMethods as google.payments.api.PaymentMethodSpecification[],
  };
}
